import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';
import { ICHING_TRIGRAMS } from '../../core/iching-trigrams.const';
import { IChingMethodTrigram } from '../../core/iching.models';
import { injectIChingTranslate } from '../iching-consult-translate';

/**
 * Kaart van één trigram in de situatie (C1): symbool, naam, Wilhelms naam,
 * rol (onder = jij, boven = omgeving), kern, betekenis in die rol, familie
 * en, als er bewegende lijnen in dit trigram liggen, het extreme. Linkerrand
 * en kleurblokje in de trigramkleur van de figuur (onder `--primary`, boven
 * `--amber`), zodat kaart en figuur aan elkaar te koppelen zijn.
 */
@Component({
  selector: 'hd-iching-trigram-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host { display: block; }
    .card {
      --_tone: var(--hd-iching-lower, var(--primary));
      display: flex; flex-direction: column; gap: 6px; height: 100%; box-sizing: border-box;
      padding: 12px 14px; border-radius: var(--radius-md, 6px);
      border-left: 4px solid var(--_tone);
      background: var(--hd-iching-card-bg, rgba(0, 0, 0, 0.035));
      font-size: 13.5px; line-height: 1.5;
    }
    .card[data-role='upper'] { --_tone: var(--hd-iching-upper, var(--amber)); }
    .head { display: flex; align-items: center; gap: 10px; }
    .symbol { font-size: 30px; line-height: 1; }
    .names { display: flex; flex-direction: column; min-width: 0; }
    .name { font-weight: 600; font-size: 15px; }
    .wilhelm { font-size: 12.5px; opacity: 0.75; }
    .role {
      align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; padding: 1px 8px; border-radius: 999px;
      border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.15)); font-size: 11.5px;
    }
    .swatch { flex: 0 0 auto; width: 10px; height: 10px; border-radius: 2px; background: var(--_tone); }
    p { margin: 0; }
    .family { font-size: 12.5px; opacity: 0.8; }
    .extreme {
      padding: 6px 10px; border-left: 2px solid var(--hd-iching-warning, #c0763a);
      background: var(--hd-iching-warning-bg, rgba(192, 118, 58, 0.08));
    }
  `,
  template: `
    <div class="card" [attr.data-trigram]="trigram().id" [attr.data-role]="role()">
      <div class="head">
        <span class="symbol" aria-hidden="true">{{ symbol() }}</span>
        <div class="names">
          <span class="name">{{ trigram().name }}</span>
          <span class="wilhelm">{{ trigram().wilhelmName }}</span>
        </div>
      </div>
      <span class="role"><span class="swatch" aria-hidden="true"></span>{{ role() === 'lower' ? t('humanDesign.iching.reading.roleLower') : t('humanDesign.iching.reading.roleUpper') }}</span>
      <p>{{ trigram().core }}</p>
      <p>{{ role() === 'lower' ? trigram().asLower : trigram().asUpper }}</p>
      <p class="family">{{ trigram().family }}, {{ trigram().gender }}</p>
      @if (extreme()) {
        <p class="extreme" data-test-id="iching-trigram-extreme">{{ trigram().extreme }}</p>
      }
    </div>
  `,
})
export class HdIChingTrigramCardComponent {
  readonly trigram = input.required<IChingMethodTrigram>();
  readonly role = input.required<'lower' | 'upper'>();
  /** Bewegende lijnen in dit trigram: toon hoe het doorslaat. */
  readonly extreme = input(false, { transform: booleanAttribute });

  protected readonly t = injectIChingTranslate();
  protected readonly symbol = computed(() => ICHING_TRIGRAMS[this.trigram().id].symbol);
}
